import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { hasRole, type UserRole } from '@collector/shared'
import { useAuth } from '../lib/auth'

interface Props {
  /** Restrict to a single role, or provide `roles` for a set. */
  role?: UserRole
  roles?: UserRole[]
  children: React.ReactNode
}

export function RequireAuth({ role, roles, children }: Props) {
  const { t } = useTranslation()
  const auth = useAuth()
  const location = useLocation()
  if (auth.status === 'loading') {
    return (
      <div className="p-8 text-sm text-stone-500 dark:text-stone-400">{t('common.loading')}</div>
    )
  }
  if (auth.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  // Force a server-issued temporary password to be replaced before the
  // operator can use anything else. The change-password screen itself is
  // exempt so they can complete the flow.
  if (auth.user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }
  const allowed: UserRole[] | undefined = roles ?? (role ? [role] : undefined)
  if (allowed && !hasRole(auth.user, ...allowed)) {
    return (
      <div className="p-8 text-sm text-red-600 dark:text-red-400">
        {t('errors.missingRole', { roles: allowed.join(', ') })}
      </div>
    )
  }
  return <>{children}</>
}
